import { type FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import DeletePlanModal from '../CalendarPage/components/DeletePlanModal';
import DeleteAllPromotionsModal from '../CalendarPage/components/DeleteAllPromotionsModal';
import MetricToolbar from './MetricToolbar';
import PlanMetricView from './PlanMetricView';
import PromoMetricView from './PromoMetricView';
import useAttributeOptions from './hooks/queries/useAttributeOptions';
import type {
    AttributeOption,
    Granularity,
    KpiViewType,
    MetricType,
    ReportView,
    SubMode,
    TimePeriod,
} from './types';

interface MetricViewProps {
    planId: string;
    planName: string;
}

const MetricView: FC<MetricViewProps> = ({ planId, planName }) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { currentPlanDetails, filterState } = usePlanDetailsContext();
    const { metricPageConfig } = useTpoGenConfig();

    // Shared toolbar state
    const [reportView, setReportView] = useState<ReportView>(metricPageConfig.defaultReportView);
    const [subMode, setSubMode] = useState<SubMode>(metricPageConfig.defaultSubMode);
    const [granularity, setGranularity] = useState<Granularity>(metricPageConfig.defaultGranularity);
    const [timePeriod, setTimePeriod] = useState<TimePeriod>(metricPageConfig.defaultTimePeriod);
    const [searchKeyword, setSearchKeyword] = useState<string>('');

    // Plan Metric state -- KPI is split into single (dimension sub-mode) and multi (metric sub-mode)
    // so each sub-mode keeps its own last-applied selection across switches.
    const [planDimension, setPlanDimension] = useState<string>(metricPageConfig.defaultPlanMetricDimension);
    const [planDimensionKpi, setPlanDimensionKpi] = useState<string>(metricPageConfig.defaultKpis[0]);
    const [planMetricKpis, setPlanMetricKpis] = useState<string[]>(() => [...metricPageConfig.defaultKpis]);
    const [attributes, setAttributes] = useState<AttributeOption[]>([]);
    const [attributeSearch, setAttributeSearch] = useState<string>('');

    // Promo Metric state
    const [promoDimensions, setPromoDimensions] = useState<string[]>(
        () => [...metricPageConfig.defaultPromoMetricDimensions],
    );
    const [promoKpis, setPromoKpis] = useState<string[]>(() => [...metricPageConfig.defaultKpis]);
    const [kpiViewType, setKpiViewType] = useState<KpiViewType>(metricPageConfig.defaultKpiViewType);
    const [metricType, setMetricType] = useState<MetricType>(metricPageConfig.defaultMetricType);

    // Plan-action modals (reused from Calendar)
    const [showDeletePlan, setShowDeletePlan] = useState(false);
    const [showDeleteAllPromotions, setShowDeleteAllPromotions] = useState(false);

    // Trend chart toggle (only valid for Plan Metrics + dimension sub-mode)
    const [showTrendChart, setShowTrendChart] = useState(false);

    useEffect(() => {
        if (reportView !== 'plan_metrics' || subMode !== 'dimension') {
            setShowTrendChart(false);
        }
    }, [reportView, subMode]);

    const enableAttributeFetch = reportView === 'plan_metrics' && subMode === 'metric';
    const attributeQuery = useAttributeOptions(
        planId,
        enableAttributeFetch ? planDimension : null,
        filterState.filters,
        attributeSearch,
    );

    const planKpis = subMode === 'dimension' ? [planDimensionKpi] : planMetricKpis;

    return (
        <>
            <MetricToolbar
                planId={planId}
                reportView={reportView}
                setReportView={setReportView}
                subMode={subMode}
                setSubMode={setSubMode}
                timePeriod={timePeriod}
                setTimePeriod={setTimePeriod}
                searchKeyword={searchKeyword}
                setSearchKeyword={setSearchKeyword}
                planDimension={planDimension}
                setPlanDimension={setPlanDimension}
                planDimensionKpi={planDimensionKpi}
                setPlanDimensionKpi={setPlanDimensionKpi}
                planMetricKpis={planMetricKpis}
                setPlanMetricKpis={setPlanMetricKpis}
                attributes={attributes}
                setAttributes={setAttributes}
                attributeOptions={attributeQuery.options}
                attributeSearch={attributeSearch}
                setAttributeSearch={setAttributeSearch}
                attributesLoading={attributeQuery.isPending || attributeQuery.isFetching}
                onAttributeDropdownScroll={
                    attributeQuery.hasNextPage ? attributeQuery.fetchNextPage : undefined
                }
                promoDimensions={promoDimensions}
                setPromoDimensions={setPromoDimensions}
                promoKpis={promoKpis}
                setPromoKpis={setPromoKpis}
                onShowDeletePlan={() => setShowDeletePlan(true)}
                onShowDeleteAllPromotions={() => setShowDeleteAllPromotions(true)}
                showTrendChart={showTrendChart}
                onToggleTrendChart={() => setShowTrendChart((v) => !v)}
            />

            {reportView === 'plan_metrics' ? (
                <PlanMetricView
                    planId={planId}
                    subMode={subMode}
                    dimension={planDimension}
                    attributes={attributes}
                    kpis={planKpis}
                    granularity={granularity}
                    setGranularity={setGranularity}
                    timePeriod={timePeriod}
                    searchKeyword={searchKeyword}
                    showTrendChart={showTrendChart}
                />
            ) : (
                <PromoMetricView
                    planId={planId}
                    dimensions={promoDimensions}
                    kpis={promoKpis}
                    timePeriod={timePeriod}
                    searchKeyword={searchKeyword}
                    kpiViewType={kpiViewType}
                    setKpiViewType={setKpiViewType}
                    metricType={metricType}
                    setMetricType={setMetricType}
                />
            )}

            <DeletePlanModal
                open={showDeletePlan}
                onClose={() => setShowDeletePlan(false)}
                planId={planId}
                planName={currentPlanDetails?.plan_name || planName}
                onSuccess={() => navigate(`/tpo-gen/plans-index${state?.fromSearch ?? ''}`)}
            />
            <DeleteAllPromotionsModal
                open={showDeleteAllPromotions}
                onClose={() => setShowDeleteAllPromotions(false)}
                planId={planId}
                planName={currentPlanDetails?.plan_name || planName || planId}
            />
        </>
    );
};

MetricView.displayName = 'MetricView';
export default MetricView;
